import styled from '@emotion/styled'
import PropTypes from 'prop-types'
import React from 'react'

import tokens from '../../design/tokens'

export const TEXT_VARIANT_STANDARD = 'standard'
export const TEXT_VARIANT_BOLD = 'bold'
export const TEXT_VARIANT_BOLD_LIGHT = 'bold-light'
export const TEXT_VARIANT_ITALIC = 'italic'
export const TEXT_VARIANT_LIGHT = 'light'
export const TEXT_VARIANT_ITALIC_LIGHT = 'italic-light'
export const TEXT_VARIANT_EXTRALIGHT = 'extralight'
export const TEXT_VARIANT_SMALL = 'small'
export const TEXT_VARIANT_SMALL_LIGHT = 'small-light'
export const TEXT_VARIANT_DANGER = 'danger'
export const TEXT_VARIANT_SMALL_DANGER = 'small-danger'
export const TEXT_VARIANT_EXTRASMALL = 'extrasmall'
export const TEXT_VARIANT_EXTRASMALL_LIGHT = 'extrasmall-light'

const handleVariant = ({ variant }) => {
  switch (variant) {
    case TEXT_VARIANT_BOLD:
      return `
        font-weight: ${tokens.fontWeight.bold};
      `
    case TEXT_VARIANT_BOLD_LIGHT:
      return `
          font-weight: ${tokens.fontWeight.bold}; 
          color:${tokens.color.text.light};
        `
    case TEXT_VARIANT_ITALIC:
      return `
        font-style: ${tokens.fontStyle.italic};
      `
    case TEXT_VARIANT_ITALIC_LIGHT:
      return `
        font-style: ${tokens.fontStyle.italic};
        color: ${tokens.color.text.light};
    `
    case TEXT_VARIANT_LIGHT:
      return `
        color: ${tokens.color.text.light};
      `
    case TEXT_VARIANT_EXTRALIGHT:
      return `
        color: ${tokens.color.text.extralight};
      `
    case TEXT_VARIANT_SMALL:
      return `
        font-size: ${tokens.fontSize.small};
      `
    case TEXT_VARIANT_SMALL_LIGHT:
      return `
        font-size: ${tokens.fontSize.small};
        color: ${tokens.color.text.light};
      `
    case TEXT_VARIANT_DANGER:
      return `
        color: ${tokens.color.text.danger};
      `
    case TEXT_VARIANT_SMALL_DANGER:
      return `
        font-size: ${tokens.fontSize.small};
        color: ${tokens.color.text.danger};
      `
    case TEXT_VARIANT_EXTRASMALL:
      return `
        font-size: ${tokens.fontSize.xsmall};
      `
    case TEXT_VARIANT_EXTRASMALL_LIGHT:
      return `
          font-size: ${tokens.fontSize.xsmall};
          color: ${tokens.color.text.light};
        `
  }
}

const StyledText = styled(
  ({ htmlElement, children, ...rest }) =>
    React.createElement(htmlElement, rest, children),
  {
    shouldForwardProp: prop => !prop.startsWith('$')
  }
)`
  font-weight: ${tokens.fontWeight.normal};
  font-size: ${tokens.fontSize.standard};
  font-style: ${tokens.fontStyle.normal};
  letter-spacing: ${tokens.letterSpacing.standard};
  line-height: ${tokens.lineHeight.standard};
  color: ${tokens.color.text.default};
  word-break: ${props => props.$wordBreak};
  overflow-wrap: ${props => props.$overflowWrap};
  white-space: ${props => props.$whiteSpace};

  ${({ $alignContent }) =>
    $alignContent ? `display: inline-flex; align-items: center;` : ''}

  ${({ $gap }) => ($gap !== undefined ? `gap: ${$gap};` : '')}
  ${({ $textTransform }) =>
    $textTransform !== undefined ? `text-transform: ${$textTransform};` : ''}

  ${handleVariant}
`

export const Text = ({
  children,
  htmlElement,
  className,
  variant,
  wordBreak,
  overflowWrap,
  whiteSpace,
  alignContent,
  gap,
  textTransform,

  ...other
}) => {
  return (
    <StyledText
      htmlElement={htmlElement}
      className={className}
      variant={variant}
      $wordBreak={wordBreak}
      $overflowWrap={overflowWrap}
      $whiteSpace={whiteSpace}
      $alignContent={alignContent}
      $gap={gap}
      $textTransform={textTransform}
      {...other}
    >
      {children}
    </StyledText>
  )
}

Text.defaultProps = {
  variant: undefined,
  htmlElement: 'p',
  wordBreak: 'normal',
  overflowWrap: 'normal',
  whiteSpace: 'normal',
  textTransform: 'none'
}

Text.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  htmlElement: PropTypes.string,
  variant: PropTypes.oneOf([
    TEXT_VARIANT_STANDARD,
    TEXT_VARIANT_BOLD,
    TEXT_VARIANT_BOLD_LIGHT,
    TEXT_VARIANT_ITALIC,
    TEXT_VARIANT_ITALIC_LIGHT,
    TEXT_VARIANT_LIGHT,
    TEXT_VARIANT_EXTRALIGHT,
    TEXT_VARIANT_DANGER,
    TEXT_VARIANT_SMALL,
    TEXT_VARIANT_SMALL_LIGHT,
    TEXT_VARIANT_SMALL_DANGER,
    TEXT_VARIANT_EXTRASMALL,
    TEXT_VARIANT_EXTRASMALL_LIGHT
  ]),
  wordBreak: PropTypes.oneOf([
    'break-all',
    'break-word',
    'inherit',
    'initial',
    'keep-all',
    'normal',
    'revert',
    'unset'
  ]),
  overflowWrap: PropTypes.oneOf([
    'normal',
    'break-word',
    'anywhere',
    'inherit',
    'initial',
    'revert',
    'revert-layer',
    'unset'
  ]),
  whiteSpace: PropTypes.oneOf([
    'normal',
    'nowrap',
    'pre',
    'pre-wrap',
    'pre-line',
    'break-spaces',
    'inherit',
    'initial',
    'revert',
    'revert-layer',
    'unset'
  ]),
  textTransform: PropTypes.oneOf([
    'capitalize',
    'uppercase',
    'lowercase',
    'none',
    'inherit',
    'initial',
    'revert',
    'revert-layer',
    'unset'
  ]),
  alignContent: PropTypes.bool,
  gap: PropTypes.string
}
